import { Power1, TweenMax } from 'gsap';
import { Sprite } from 'pixi.js';
import { GameItemConfig } from '../../../Config/Items';
import { createBitmapText, scaleToFit } from '../../../Utils/Utils';
import { Animation } from '../../Animations/Animation';
import { BoosterPrice } from '../../Shared/BoosterPrice';
import { UIButton } from '../../Shared/Buttons/UIButton';
import { FreeBooster } from '../../Shared/FreeBooster';

export class BoosterButton extends UIButton {
    public boosterId: string;
    private numTxt: PIXI.extras.BitmapText;
    private numBg: Sprite;
    private plus: Sprite;
    private isLocked: boolean = false;
    private pulse: boolean = false;
    private lockedIcon: Sprite;
    private boosterIcon: Sprite;
    private activeBg: PIXI.Sprite;
    private lockedBg: PIXI.Sprite;
    private isActive: boolean;
    private activeMask: PIXI.Graphics;
    private revealPercent: number = 0;
    private revealAnimation: Animation;

    constructor(id: string, active: boolean) {
        super('commonui/button-booster-idle');
        this.boosterId = id;

        const locked = (this.lockedBg = new Sprite(
            PIXI.utils.TextureCache['commonui/button-booster-locked']
        ));
        locked.anchor.set(0.5);
        locked.scale.set(scaleToFit(locked.width, locked.height, locked.width, locked.height));

        locked.alpha = 0;
        this.lockedIcon = locked;

        const { bg } = this;
        bg.anchor.set(0.5);
        bg.scale.set(scaleToFit(bg.width, bg.height, bg.width, bg.height));

        const activeBg = (this.activeBg = new Sprite(
            PIXI.utils.TextureCache['commonui/button-booster-active']
        ));
        activeBg.anchor.set(0.5);
        activeBg.scale.set(
            scaleToFit(activeBg.width, activeBg.height, activeBg.width, activeBg.height)
        );
        activeBg.alpha = 0;

        const buttonMask = (this.activeMask = new PIXI.Graphics());
        buttonMask.beginFill(0x000000);
        activeBg.mask = buttonMask;
        this.addChild(buttonMask);

        const icon = new Sprite(PIXI.utils.TextureCache['mapui/booster-' + id.toLowerCase()]);
        icon.anchor.set(0.5);
        icon.scale.set(scaleToFit(icon.width, icon.height, bg.width * 0.7, bg.height * 0.7));
        this.boosterIcon = icon;

        this.disableAnimation.addToLast(TweenMax.to(icon, 0.1, { pixi: { saturation: 0.2 } }));

        this.addChild(bg, activeBg, icon, locked);
        this.setActive(active, 1);
    }

    public setIconVisible(isVisible: boolean) {
        this.boosterIcon.alpha = isVisible ? 1 : 0;
    }

    public setActive(isActive: boolean, ms: number = 1000) {
        if (this.isActive === isActive) {
            return;
        }
        TweenMax.killTweensOf(this.scale);
        TweenMax.killTweensOf(this.activeBg);
        this.isActive = isActive;
        if (isActive) {
            this.revealActive(1);
            TweenMax.to(this.scale, (ms / 1000) * 0.5, {
                x: 1.1,
                y: 1.1,
                ease: Power1.easeOut,
                repeat: 1,
                yoyo: true,
            });
            TweenMax.to(this.activeBg, ms / 1000, { alpha: 1 });
        } else {
            this.activeBg.alpha = 0;
        }
    }

    public revealActive(percent: number) {
        percent = Math.min(1, Math.max(0, percent));
        if (percent === this.revealPercent) {
            return;
        }

        if (this.revealAnimation) {
            this.revealAnimation.kill();
        }
        const diff = (this.revealPercent * 10 - percent * 10) / 10;
        const r = this.revealPercent;
        if (diff > 0) {
            const stepAmt = 0.01;
            const numSteps = diff / stepAmt;
            let stepNum = 0;
            const animation = (this.revealAnimation = new Animation({}));
            while (stepNum < numSteps) {
                const stepPercent = r - stepAmt * (stepNum + 1);
                animation.add(() => this.drawMask(stepPercent));
                animation.addDelay(20);
                stepNum++;
            }
            animation.play();
        } else {
            this.drawMask(percent);
        }

        this.revealPercent = percent;
    }

    public callAttention() {
        this.pulse = true;
        TweenMax.to(this.scale, 0.4, {
            x: 1.05,
            y: 1.05,
            ease: Power1.easeOut,
            repeat: -1,
            yoyo: true,
        });
    }

    public stopAttention() {
        if (!this.pulse) {
            return;
        }
        this.pulse = false;
        TweenMax.killTweensOf(this.scale);
        this.scale.set(1);
    }

    public showQuantity(n: number, tutorial: boolean) {
        if (this.plus && tutorial) {
            this.removeChild(this.plus);
            this.plus = null;
        }
        if (this.isLocked) {
            return;
        }
        const { bg } = this;
        if (!this.numTxt) {
            this.numBg = new PIXI.Sprite(PIXI.utils.TextureCache['gameui/booster-amount-bg']);
            this.numBg.scale.set((bg.width * 0.4) / this.numBg.width);
            this.numBg.anchor.set(0.5);
            this.numBg.x = -bg.width * 0.3;
            this.numBg.y = bg.height * 0.25;
            this.numTxt = createBitmapText(
                n.toString(10),
                new PIXI.Rectangle(
                    -bg.width * 0.3,
                    bg.height * 0.3,
                    bg.width * 0.2,
                    bg.height * 0.2
                ),
                'riffic'
            );
            this.numTxt.anchor = 0.5;
            this.numTxt.align = 'center';
            //@ts-ignore
            this.addChild(this.numBg, this.numTxt);
        }

        if (!this.plus) {
            const boosterPrice = GameItemConfig[this.boosterId].price;
            if (tutorial) {
                this.plus = new FreeBooster(bg.width * 0.5, bg.height * 0.5);
            } else {
                this.plus = new BoosterPrice(bg.width * 0.5, bg.height * 0.5, boosterPrice);
            }
            this.plus.y = bg.height * 0.41;
            this.plus.scale.set(1.6);
            this.addChild(this.plus);
        }

        if (n > 0 && !tutorial) {
            this.numTxt.text = n.toString();
            this.numTxt.alpha = 1;
            this.numBg.alpha = 1;
            this.plus.alpha = 0;
        } else {
            this.numTxt.alpha = 0;
            this.numBg.alpha = 0;
            this.plus.alpha = 1;
        }
    }

    public setLocked(isLocked: boolean) {
        this.isLocked = isLocked;
        this.lockedIcon.alpha = isLocked ? 1 : 0;
        this.lockedBg.alpha = isLocked ? 1 : 0;
        this.setLockAlpha(this.numTxt, isLocked);
        this.setLockAlpha(this.numBg, isLocked);
        this.setLockAlpha(this.plus, isLocked);
    }

    protected setEnabled(enabled: boolean) {
        if (this.isLocked) {
            return;
        }
        super.setEnabled(enabled);
    }

    private drawMask(percent: number) {
        this.activeMask.clear();
        this.activeMask.beginFill(0x000000);
        const degs: number = 360 * percent;
        const rads = degs * (Math.PI / 180);
        const start = -90 * (Math.PI / 180);
        const fillWidth = this.activeBg.width / 2;
        this.activeMask.moveTo(0, 0);
        this.activeMask.arc(0, 0, fillWidth, start, rads + start);
        this.activeMask.currentPath.shape.closed = false;
    }

    private setLockAlpha(part: PIXI.Sprite | PIXI.extras.BitmapText, isLocked: boolean) {
        if (!part) {
            return;
        }
        part.alpha = isLocked ? 0 : 1;
    }
}
